import { Trans, useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import React, { useMemo } from "react";
import Loader from "../../components/ui/loader";
import Style from "./attendees-list.module.scss";
import { Card, Icon, WeezHeader } from "@weezevent/nacre";
import Filter, { IdentificationType } from "./components/filter";
import SmartTable from "../../components/smart-table/smart-table";
import EmptyTable, { EmptyTableContext } from "../../components/empty-table";
import { isEmpty } from "lodash";
import TextTableCell from "../../components/text-table-cell/textTableCell";
import { SORTING } from "../../constants/sorting";
import PropTypes from "prop-types";
import Status from "./ui/status";
import useQueryParams from "../../utils/useQueryParams";
import { useHistory } from "react-router-dom";
import useAttendeeList from "./hooks/useAttendeeList";
import useRouterContext from "../../context/routerContext";
import QRCodeScanModal from "../../components/qr-code-scan-modal";
import Attendee from "models/attendee";
import useEventAttendeeList from "./hooks/useEventAttendeeList";
import {
	getCheckpoints,
	getEventConfig,
	getEventConfigCheckpoints,
	getEvents
} from "../../api";
import useSearchInputFocus from "hooks/useSearchInputFocus";
import {
	filtersToParams,
	paramsToFilters,
	extractFormFieldFilter
} from "utils/filterParamsMethods";
import { useGetAllZones } from "api/api-hooks/event-config/zones/use-get-all-zones";
import { useEventPeriodsList } from "api/api-hooks/useEventPeriodsList";
import { useGetAllPopulations } from "api/api-hooks/event-config/event-config-populations/use-get-all-populations";
import { useGetFormsWithEntryUsage } from "api/api-hooks/use-get-forms-with-entry-usage";
import { PAGE_SIZE_OPTIONS } from "../checkpoint/constants";
import PageSizeDropdown from "components/page-size-dropdown";
import AttendeesBatchActions from "./components/batchs-actions/attendees-batch-actions";
import useUserCapacity from "utils/rights";
import { CAPACITIES } from "constants/capacities";
import { useBrowserTabTitle } from "hooks/useBrowserTabTitle";

const ATTENDEE_FILTERS = [
	"search",
	"searchType",
	"checkpoint_ids",
	"event_ids",
	"zone_ids",
	"period_ids",
	"population_ids",
	"sortingDirection",
	"sortingColumn",
	"status",
	"form_field_filter",
	"page",
	"pageSize"
];

const AttendeesList = ({ organization, eventId }) => {
	const { t } = useTranslation();
	const organizationId = organization.id();
	useBrowserTabTitle(
		eventId
			? `O${organizationId} • E${eventId} • ${t(
					"access.titles.attendees"
			  )}`
			: `O${organizationId} • ${t("access.titles.attendees")}`
	);
	const { path } = useRouterContext();

	const canManageBatchActions = useUserCapacity(
		organization.id(),
		CAPACITIES.ATTENDEES.MANAGE
	);

	const [params, setParams] = useQueryParams();

	// describes whether initial filters have been populated.
	const [initFilters, setInitFilters] = React.useState(!isEmpty(params));

	// if there were no initial filters, they will be populated, but we must skip the first parameter update
	const [
		skippedFirstParamUpdate,
		setSkippedFirstParamUpdate
	] = React.useState(isEmpty(params));

	const [isScanQRCodeModalOpen, setIsScanQRCodeModalOpen] = React.useState(
		false
	);

	const [search, setSearch] = React.useState(params.search);
	const [searchType, setSearchType] = React.useState(
		params?.searchType || IdentificationType.ALL
	);

	useSearchInputFocus(search);

	const [sorting, setSorting] = React.useState({
		direction: params?.sortingDirection,
		column: params?.sortingColumn
	});

	const [attendeeSelection, setAttendeeSelection] = React.useState([]);

	const history = useHistory();
	const useEventConfig = (organizationId, eventId) => {
		return useQuery({
			queryKey: ["eventConfig", organizationId, eventId],
			queryFn: async () =>
				getEventConfig(organizationId, eventId).then(
					resp => resp.response
				),
			enabled: !!eventId
		});
	};
	const eventConfig = useEventConfig(organization.id(), eventId);
	const useEvents = (organizationId, eventId, eventConfig) => {
		return useQuery({
			queryKey: ["events", organizationId, eventId],
			queryFn: async () => {
				const eventIds = eventConfig?.events?.concat(eventId) || [];
				return getEvents(organizationId, {
					event_ids: eventIds.join(",")
				}).then(resp => resp.response);
			},
			enabled: eventId === null ? true : !!eventId && !!eventConfig
		});
	};

	const events = useEvents(organization.id(), eventId, eventConfig.data);

	const filteredEvents = React.useMemo(() => {
		if (!events?.data) return [];

		if (eventId !== null) {
			return events.data;
		}

		return events.data.filter(event => !event.is_closed);
	}, [events, eventId]);

	const eventIds = useMemo(
		() => new Set(filteredEvents.map(event => String(event.id))),
		[filteredEvents]
	);

	const [filters, setFilters] = React.useState(
		paramsToFilters(ATTENDEE_FILTERS, {
			checkpoint_ids: params?.checkpoint_ids,
			event_ids: params?.event_ids || eventIds,
			zone_ids: params?.zone_ids,
			period_ids: params?.period_ids,
			population_ids: params?.population_ids,
			status: params?.status || Attendee.STATUS.ACTIVE,
			page: params?.page,
			form_field_filter: params?.form_field_filter,
			pageSize: params?.pageSize,
			searchType: params?.searchType || IdentificationType.ALL
		})
	);

	const currentPage = filters?.page ?? 0;
	const pageSize = filters?.pageSize ?? 50;

	const checkpoints = useQuery(
		["checkpoints", organization.id(), eventId],
		async () => {
			return eventId
				? getEventConfigCheckpoints(organizationId, eventId, {
						limit: 50,
						offset: 0,
						only_stats: true,
						archived: false
				  }).then(resp => {
						return resp.response;
				  })
				: getCheckpoints(organizationId, {
						ordering: "active_first,-start_date",
						archived: false
				  }).then(resp => {
						return resp.data;
				  });
		}
	);

	const zonesQuery = useGetAllZones({ eventId });
	const periodsQuery = useEventPeriodsList({ eventId });
	const populationsQuery = useGetAllPopulations({ eventId });
	const formsQuery = useGetFormsWithEntryUsage({
		organizationId: organization.id(),
		eventId
	});

	React.useEffect(() => {
		// Set the default filters if there is no params in the url on the first render
		if (
			!initFilters &&
			!events.isLoading &&
			!isEmpty(filteredEvents) &&
			isEmpty(params)
		) {
			setSkippedFirstParamUpdate(false);
			setInitFilters(true);

			const defaultStatus = params?.status || Attendee.STATUS.ACTIVE;
			// Set other filters as needed
			if (eventIds.size < 100) {
				setFilters(prevFilters => ({
					...prevFilters,
					event_ids: eventIds,
					status: defaultStatus
				}));
			} else {
				setFilters(prevFilters => ({
					...prevFilters,
					event_ids: new Set() // Clear event_ids if there are too many events
				}));
			}
		}
	}, [
		eventIds,
		filteredEvents,
		params,
		initFilters,
		setParams,
		setFilters,
		events.isLoading
	]);

	const filtersIsLoading = React.useMemo(() => {
		return (
			filteredEvents.isLoading ||
			checkpoints.isLoading ||
			zonesQuery.isLoading ||
			periodsQuery.isLoading ||
			populationsQuery.isLoading ||
			formsQuery.isLoading
		);
	}, [
		filteredEvents,
		checkpoints,
		zonesQuery,
		periodsQuery,
		populationsQuery,
		formsQuery
	]);
	const useAttendeeListHook = eventId
		? useEventAttendeeList
		: useAttendeeList;
	const {
		data,
		isLoading,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		totalCount,
		refetch
	} = useAttendeeListHook({
		organization,
		eventId,
		search: search || undefined,
		searchType: filters?.searchType || searchType,
		sorting,
		filters,
		enabled: !filtersIsLoading,
		page: currentPage,
		pageSize
	});

	const nextPage = React.useCallback(() => {
		if (hasNextPage && !isFetchingNextPage) {
			return fetchNextPage();
		}
	}, [hasNextPage, isFetchingNextPage, fetchNextPage]);

	const onPageChange = React.useCallback(
		pageNumber => {
			setFilters(prevFilters => {
				const updatedFilters = {
					...prevFilters,
					page: pageNumber
				};
				setParams(
					filtersToParams(ATTENDEE_FILTERS, {
						...params,
						...updatedFilters,
						form_field_filter: prevFilters?.form_field_filter
					})
				);
				return updatedFilters;
			});
		},
		[params, setParams]
	);

	const resetPagination = React.useCallback(() => {
		setFilters(prevFilters => {
			const updatedFilters = {
				...prevFilters,
				page: 0
			};
			setParams(
				filtersToParams(ATTENDEE_FILTERS, {
					...params,
					...updatedFilters,
					form_field_filter: prevFilters?.form_field_filter
				})
			);
			return updatedFilters;
		});
	}, [params, setParams]);

	const openDetails = React.useCallback(
		({ id }) => {
			history.push(`${path}/attendees/${id}`);
		},
		[history, path]
	);

	const actualFormFieldFilter = extractFormFieldFilter(
		data.form_field_filter
	);

	const onChangeFilters = data => {
		if (!data) {
			data = {};
		}
		setFilters(data);
		if (data.searchType && data.searchType !== searchType) {
			setSearchType(data.searchType);
		}
		resetPagination();
		if (!skippedFirstParamUpdate) {
			setSkippedFirstParamUpdate(true);
			return; // do not write parameters for the first filter update
		}

		setParams(
			filtersToParams(ATTENDEE_FILTERS, {
				...params,
				...filters,
				checkpoint_ids: data.checkpoint_ids,
				event_ids: data.event_ids,
				zone_ids: data.zone_ids,
				period_ids: data.period_ids,
				population_ids: data.population_ids,
				status: data.status,
				form_field_filter: actualFormFieldFilter,
				searchType: data.searchType,
				page: data.page,
				pageSize: data.pageSize
			})
		);
	};

	const handlePageSizeChange = value => {
		setFilters(prevFilters => {
			const updatedFilters = {
				...prevFilters,
				page: 0,
				pageSize: value
			};
			setParams(
				filtersToParams(ATTENDEE_FILTERS, {
					...params,
					...updatedFilters,
					form_field_filter: prevFilters?.form_field_filter
				})
			);
			return updatedFilters;
		});
	};

	const onChangeSearch = data => {
		if (!data) {
			setSearch("");
			return;
		}
		setSearch(data);
		resetPagination();
		setParams(
			filtersToParams(ATTENDEE_FILTERS, {
				...params,
				...filters,
				search: data || undefined,
				searchType: filters?.searchType || searchType,
				form_field_filter: filters?.form_field_filter
			})
		);
	};

	const onChangeSearchType = type => {
		setSearchType(type);
		setFilters(prevFilters => ({
			...prevFilters,
			searchType: type
		}));
		resetPagination();
		setParams(
			filtersToParams(ATTENDEE_FILTERS, {
				...params,
				...filters,
				search,
				searchType: type,
				form_field_filter: filters?.form_field_filter
			})
		);
	};

	const onClickSorting = data => {
		setSorting(data);
		resetPagination();
		setParams(
			filtersToParams(["sortingDirection", "sortingColumn"], {
				...params,
				sortingColumn: data?.column,
				sortingDirection: data?.direction
			})
		);
	};

	const emptyTableContext =
		!search && isEmpty(filters)
			? EmptyTableContext.NO_RESULTS
			: EmptyTableContext.SEARCH;

	const columns = React.useMemo(
		() => [
			{
				key: "attendee",
				label: t(
					"access.attendees.attendeesList.tableColumns.attendee"
				),
				render: (_, { first_name, last_name }) => (
					<TextTableCell
						className={Style["attendees-list__column"]}
						title={[last_name, first_name]
							.filter(Boolean)
							.join(" ")}
					/>
				),
				preferredSortingType: SORTING.TYPES.STRING,
				sortable: true,
				width: "160px"
			},
			{
				key: "event_name",
				label: t(
					"access.attendees.attendeesList.tableColumns.event_name"
				),
				sortable: true,
				width: "160px",
				render: value => (
					<TextTableCell
						className={Style["attendees-list__column"]}
						title={value}
					/>
				)
			},
			{
				key: "barcode",
				label: t("access.attendees.attendeesList.tableColumns.barcode"),
				sortable: true,
				width: "160px",
				render: value => (
					<TextTableCell
						className={Style["attendees-list__column"]}
						title={value}
					/>
				)
			},
			{
				key: "rate_name",
				label: t(
					"access.attendees.attendeesList.tableColumns.rate_name"
				),
				sortable: true,
				width: "160px",
				render: value => (
					<TextTableCell
						className={Style["attendees-list__column"]}
						title={value}
					/>
				)
			},
			{
				key: "removed",
				label: t("access.attendees.attendeesList.tableColumns.status"),
				width: "100px",
				render: value => <Status removed={value} />
			},
			{
				key: "has_scans",
				label: t(
					"access.attendees.attendeesList.tableColumns.validated.title"
				),
				render: value => (
					<Icon name={value ? "check-circle" : "cross-circle"} />
				),
				customSortingLabels: {
					[SORTING.DIRECTIONS.ASC]: t(
						"access.attendees.attendeesList.tableColumns.validated.sorting.last"
					),
					[SORTING.DIRECTIONS.DESC]: t(
						"access.attendees.attendeesList.tableColumns.validated.sorting.first"
					)
				},
				sortable: true,
				width: "40px"
			}
		],
		[t]
	);

	if (filtersIsLoading || !initFilters) {
		return (
			<div className={Style["attendees-list"]}>
				<WeezHeader title={t("access.attendees.attendeesList.title")}>
					{
						<Trans i18nKey="access.attendees.attendeesList.subtitle" />
					}
				</WeezHeader>
				<Loader />
			</div>
		);
	}

	const handleQRDetected = data => {
		setSearch(data);
		resetPagination();
		setParams(
			filtersToParams(ATTENDEE_FILTERS, {
				...params,
				...filters,
				search: data,
				searchType: filters?.searchType || searchType,
				form_field_filter: filters?.form_field_filter
			})
		);
	};

	const handleSuccessBatchAction = () => {
		refetch?.();
	};

	return (
		<div className={Style["attendees-list"]}>
			<QRCodeScanModal
				onClose={() => setIsScanQRCodeModalOpen(false)}
				onScan={handleQRDetected}
				isOpen={isScanQRCodeModalOpen}
			/>
			<WeezHeader title={t("access.attendees.attendeesList.title")}>
				{<Trans i18nKey="access.attendees.attendeesList.subtitle" />}
			</WeezHeader>
			<Card className={Style["attendees-list__filter"]}>
				<Card.Content>
					<Filter
						initialValues={filters}
						defaultValues={null}
						searchValue={search}
						onChangeSearchValue={onChangeSearch}
						searchType={searchType}
						onChangeSearchType={onChangeSearchType}
						totalCount={String(totalCount)}
						onChange={onChangeFilters}
						events={events.data}
						checkpoints={checkpoints.data}
						zones={zonesQuery.data}
						periods={periodsQuery.data}
						populations={populationsQuery.data}
						formsData={formsQuery.data}
						onClickQRCodeButton={() =>
							setIsScanQRCodeModalOpen(true)
						}
					/>
					<div className={Style["attendee-batch-actions-button"]}>
						{canManageBatchActions && (
							<AttendeesBatchActions
								attendeeIds={attendeeSelection}
								onSuccess={handleSuccessBatchAction}
								setAttendeeIds={setAttendeeSelection}
								eventId={eventId}
							/>
						)}
						<div className={Style["page-size-wrapper"]}>
							<PageSizeDropdown
								pageSize={pageSize}
								onChange={handlePageSizeChange}
								pageSizeOptions={PAGE_SIZE_OPTIONS}
								labelRender={value =>
									t(
										"access.attendees.attendeesList.pageSizeDropdownLabel",
										{
											value
										}
									)
								}
							/>
						</div>
					</div>
				</Card.Content>
			</Card>
			<SmartTable
				rowSelection={{
					selected: attendeeSelection,
					onChangeSelection: setAttendeeSelection
				}}
				loading={isLoading}
				data={data || []}
				rowKey="id"
				columns={columns}
				onClickSorting={onClickSorting}
				currentSorting={sorting}
				onLastRowVisible={nextPage}
				onRowClick={openDetails}
				lastRowLoading={isFetchingNextPage}
				emptyComponent={
					<EmptyTable
						label={t(
							"access.attendees.attendeesList.emptyList.single"
						)}
						labelPlural={t(
							"access.attendees.attendeesList.emptyList.plural"
						)}
						context={emptyTableContext}
					/>
				}
				customCellClassFn={(attendee, column) => {
					if (column !== "removed" && attendee.removed) {
						return "disabledRowContent";
					}

					return undefined;
				}}
			>
				<SmartTable.Paginator
					pageSize={pageSize}
					totalCount={totalCount}
					currentPage={currentPage}
					onPageChange={onPageChange}
				/>
			</SmartTable>
		</div>
	);
};

AttendeesList.propTypes = {
	organization: PropTypes.object.isRequired,
	eventId: PropTypes.number
};

AttendeesList.defaultProps = {
	eventId: null
};

export default AttendeesList;
